
import React from 'react';
import {
    Trophy,
    Sparkles,
    Crown,
    Swords,
    Star,
    Flame,
    BookOpen,
    User,
    Medal,
    Gem,
    Shield
} from 'lucide-react';

interface LevelBadgeProps {
    level: number;
    size?: 'sm' | 'md' | 'lg';
    showTitle?: boolean;
    className?: string; // Permitir classes personalizadas
}

export const LevelBadge: React.FC<LevelBadgeProps> = ({ level, size = 'md', showTitle = false, className = '' }) => {
    // Configuração de Cores e Títulos baseado no nível
    const getLevelConfig = (lvl: number) => {
        if (lvl >= 1000) return {
            bg: 'bg-gradient-to-br from-red-600 via-rose-600 to-red-800',
            text: 'text-white',
            border: 'border-rose-400/50',
            ring: 'ring-rose-500/30',
            shadow: 'shadow-[0_0_15px_rgba(244,63,94,0.6)]',
            title: 'Monarca',
            icon: Gem
        };
        if (lvl >= 500) return {
            bg: 'bg-gradient-to-br from-fuchsia-600 via-purple-600 to-fuchsia-800',
            text: 'text-white',
            border: 'border-fuchsia-400/50',
            ring: 'ring-fuchsia-500/30',
            shadow: 'shadow-[0_0_12px_rgba(217,70,239,0.5)]',
            title: 'Entidade',
            icon: Sparkles
        };
        if (lvl >= 200) return {
            bg: 'bg-gradient-to-br from-indigo-600 via-blue-600 to-indigo-800',
            text: 'text-white',
            border: 'border-indigo-400/50',
            ring: 'ring-indigo-500/30',
            shadow: 'shadow-[0_0_10px_rgba(99,102,241,0.5)]',
            title: 'Grão-Mestre',
            icon: Crown
        };
        if (lvl >= 100) return {
            bg: 'bg-gradient-to-br from-amber-500 via-orange-500 to-amber-700',
            text: 'text-white',
            border: 'border-amber-300/50',
            ring: 'ring-amber-500/30',
            shadow: 'shadow-[0_0_10px_rgba(245,158,11,0.5)]',
            title: 'Mestre',
            icon: Swords
        };
        if (lvl >= 50) return {
            bg: 'bg-gradient-to-br from-yellow-500 to-yellow-700',
            text: 'text-white',
            border: 'border-yellow-300/50',
            ring: 'ring-yellow-500/30',
            shadow: 'shadow-[0_0_8px_rgba(234,179,8,0.4)]',
            title: 'Crítico',
            icon: Star
        };
        if (lvl >= 30) return {
            bg: 'bg-gradient-to-br from-purple-500 to-purple-700',
            text: 'text-white',
            border: 'border-purple-400/50',
            ring: 'ring-purple-500/30',
            shadow: 'shadow-[0_0_8px_rgba(168,85,247,0.3)]',
            title: 'Viciado',
            icon: Flame
        };
        if (lvl >= 10) return {
            bg: 'bg-gradient-to-br from-cyan-600 to-cyan-800',
            text: 'text-white',
            border: 'border-cyan-400/50',
            ring: 'ring-cyan-500/30',
            shadow: 'shadow-[0_0_5px_rgba(6,182,212,0.3)]',
            title: 'Aprendiz',
            icon: BookOpen
        };
        return {
            bg: 'bg-gradient-to-br from-slate-600 to-slate-800',
            text: 'text-slate-100',
            border: 'border-slate-500/50',
            ring: 'ring-slate-500/30',
            shadow: 'shadow-none',
            title: 'Novato',
            icon: null,
        };
    };

    const config = getLevelConfig(level);
    const Icon = config.icon;

    // Tamanhos
    // Ajustando para ser mais compacto e "pill" shape
    const containerClasses = {
        sm: 'h-5 min-w-[36px] px-1.5 text-[10px] gap-1', // Mais compacto para overlay
        md: 'h-6 min-w-[48px] px-2 text-xs gap-1.5',
        lg: 'h-8 min-w-[64px] px-3 text-sm gap-2',
    }[size];

    const iconSizes = {
        sm: 10,
        md: 12,
        lg: 16
    }[size];

    return (
        <div className={`inline-flex items-center gap-2 ${className}`}>
            <div
                className={`
          flex items-center justify-center
          ${containerClasses}
          ${config.bg} ${config.text} ${config.shadow}
          rounded-full border ${config.border} border-t-white/20
          ring-1 ${config.ring}
          font-bold tracking-wide
          backdrop-blur-md bg-opacity-95
          shadow-lg
          transition-transform hover:scale-105 duration-200
        `}
            >
                {Icon && <Icon size={iconSizes} strokeWidth={2.5} className="drop-shadow-sm opacity-90" />}
                <span className="drop-shadow-sm leading-none pt-[1px]">{level}</span>
            </div>

            {showTitle && (
                <span className={`text-xs font-semibold tracking-wide ${config.text === 'text-white' ? 'text-gray-300' : 'text-gray-500'}`}>
                    {config.title}
                </span>
            )}
        </div>
    );
};
